import { AlertCircle, Edit2 } from 'lucide-react';
import type { LabTestCollectionMethodT } from '@lotushealth/lotushealth_js_sdk/schemas';

import type { ICD10CodeT } from '@/entities/labs';
import { formatIcd10CodeForDisplay } from '@/shared/lib/formatters';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';

import type { NativeLabOrderSelectionItem } from '../../model/nativeLabOrderSelection.js';

interface RowProps {
  label: string;
  children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

export interface NativeLabOrderReviewStepProps {
  method: LabTestCollectionMethodT;
  selectedItems: NativeLabOrderSelectionItem[];
  answeredAoeQuestionCount: number;
  icdCodes: ICD10CodeT[];
  priority: boolean;
  notes: string;
  errorMessage: string | null;
  onEditTests?: () => void;
  onEditDetails?: () => void;
}

export function NativeLabOrderReviewStep({
  method,
  selectedItems,
  answeredAoeQuestionCount,
  icdCodes,
  priority,
  notes,
  errorMessage,
  onEditTests,
  onEditDetails,
}: NativeLabOrderReviewStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <span className="text-sm font-medium">Order Summary</span>
          <div className="flex gap-1">
            {onEditTests ? (
              <Button type="button" variant="ghost" size="sm" onClick={onEditTests}>
                <Edit2 className="mr-1 size-3" /> Edit tests
              </Button>
            ) : null}
            {onEditDetails ? (
              <Button type="button" variant="ghost" size="sm" onClick={onEditDetails}>
                <Edit2 className="mr-1 size-3" /> Edit details
              </Button>
            ) : null}
          </div>
        </div>

        <dl className="space-y-3">
          <Row label="Method">{method.replace(/_/g, ' ')}</Row>
          <Row label="Tests">
            <ul className="divide-y rounded-md border">
              {selectedItems.map((item) => (
                <li key={`${item.source}:${item.id}`} className="px-3 py-2">
                  <div className="font-medium">{item.name}</div>
                  {item.components.length > 0 &&
                  (item.kind === 'panel' || item.kind === 'configured_test') ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Includes {item.components.map(({ name }) => name).join(', ')}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </Row>

          {answeredAoeQuestionCount > 0 && (
            <Row label="AOE Answered">{answeredAoeQuestionCount} question(s)</Row>
          )}

          {icdCodes.length > 0 && (
            <Row label="ICD-10 Codes">
              <span className="flex flex-wrap gap-1">
                {icdCodes.map((code) => (
                  <span key={code.code} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    <span className="font-semibold">{formatIcd10CodeForDisplay(code.code)}</span>{' '}
                    <span className="opacity-80">{code.description}</span>
                  </span>
                ))}
              </span>
            </Row>
          )}

          {priority && (
            <Row label="Priority">
              <span className="flex items-center gap-1 font-medium text-amber-600">
                <AlertCircle className="size-4" /> STAT
              </span>
            </Row>
          )}

          {notes.trim() && <Row label="Notes">{notes}</Row>}
        </dl>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Submission issue</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
